<template>
  <div>
    <index-card :loading="allLoading">
      <template slot="header">
        <div class="d-flex align-center">
          <v-btn text icon large to="/" exact>
            <v-icon class="white--text">mdi-arrow-left</v-icon>
          </v-btn>
        </div>
        <div class="d-flex align-center">
          <span class="headline white--text">
            {{ selectedPickupLocationId ? selectedPickupLocationLabel : "Selecione um local de retira" }}
          </span>
          <btn-create-data-export
            v-if="selectedPickupLocationId"
            class="mx-2"
            api_model="ShippingRate"
            action="ShippingRates"
            :filters="exportFilters"
            :small="true"
          />
          <v-btn text icon @click="getData" exact>
            <v-icon class="white--text">mdi-refresh</v-icon>
          </v-btn>
        </div>
      </template>

      <template slot="subheader">
        <v-autocomplete
          v-if="!selectedPickupLocationId"
          v-model="selectedBrandId"
          :items="brands"
          prepend-icon="mdi-factory"
          label="Marca"
          item-text="name"
          item-value="id"
          clearable
          class="mx-1"
        />
        <div v-else class="shipping-rate-region-filter-wrap">
          <filter-data
            with_text_search
            text_search_label="Pesquisar por nome da região"
            :with_client="false"
            :with_date_start="false"
            :with_date_end="false"
            @doneFilter="handleDoneRegionFilter"
          />
        </div>
      </template>

      <template slot="data_table">
        <div v-if="!selectedPickupLocationId" class="pa-4">
          <v-row>
            <v-col
              v-for="brandGroup in pickupLocationGroups"
              :key="brandGroup.brand_key"
              cols="12"
              md="6"
              lg="4"
            >
              <v-card outlined class="pickup-location-group-card">
                <v-card-title class="subtitle-1 font-weight-bold">
                  {{ brandGroup.brand_name }}
                </v-card-title>
                <v-divider />
                <v-list dense>
                  <v-list-item
                    v-for="pickupLocation in brandGroup.pickup_locations"
                    :key="pickupLocation.id"
                    @click="selectPickupLocation(pickupLocation)"
                  >
                    <v-list-item-icon>
                      <v-icon color="primary">mdi-map-marker</v-icon>
                    </v-list-item-icon>
                    <v-list-item-content>
                      <v-list-item-title>{{ pickupLocation.attributes.name }}</v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>
                </v-list>
              </v-card>
            </v-col>
          </v-row>
        </div>

        <div v-else>
          <div class="d-flex my-2">
            <v-btn small text color="primary" @click="clearSelectedPickupLocation">
              <v-icon left small>mdi-arrow-left</v-icon>
              Trocar local de retira
            </v-btn>
            <v-spacer />
            <v-btn
              small
              class="mr-2"
              :color="isEditMode ? 'grey' : 'primary'"
              :outlined="isEditMode"
              @click="toggleEditMode"
            >
              <v-icon left small>{{ isEditMode ? "mdi-eye" : "mdi-pencil" }}</v-icon>
              {{ isEditMode ? "Visualização" : "Editar" }}
            </v-btn>
          </div>
          <v-card outlined class="mb-4">
            <v-card-title class="d-flex justify-space-between align-center">
              <v-spacer />
              <v-btn
                v-if="userCanEdit && isEditMode && hasFilledDraftRows"
                small
                color="primary"
                dark
                :loading="loading.bulk_saving"
                @click="createFilledShippingRates"
              >
                Salvar tudo
              </v-btn>
            </v-card-title>
            <v-card-text>
              <shipping-rate-region-table
                :search="search"
                :global-search="Boolean(search)"
                :edit-mode="isEditMode"
                :refresh-counter="regionTreeRefreshCounter"
                :drafts-by-region-id="shippingRateDraftsByRegionId"
                :existing-by-region-id="existingShippingRatesByRegionId"
                @update-draft="handleUpdateDraft"
                @save-row="saveSingleDraftRow"
              />
            </v-card-text>
          </v-card>
        </div>
      </template>
    </index-card>

    <dialog-loading loadingText="Salvando fretes" v-if="loading.bulk_saving" />
  </div>
</template>

<script>
import Api from "@/api/index"
import BtnCreateDataExport from "@/components/data_export/BtnCreateDataExport.vue"

export default {
  name: "ShippingRates",
  components: {
    IndexCard: () => import("@/_backoffice/_registrations/components/shared/index/IndexCard.vue"),
    BtnCreateDataExport,
    FilterData: () => import("@/components/shared/FilterData.vue"),
    ShippingRateRegionTable: () => import("@/_backoffice/_registrations/components/shipping_rates/ShippingRateRegionTable.vue"),
  },
  data() {
    return {
      loading: {
        brands: false,
        pickup_locations: false,
        shipping_rates: false,
        bulk_saving: false,
      },
      search: null,
      brands: [],
      pickupLocations: [],
      shippingRates: [],
      selectedBrandId: null,
      selectedPickupLocationId: null,
      isEditMode: false,
      regionTreeRefreshCounter: 0,
      shippingRateDraftsByRegionId: {},
    }
  },
  computed: {
    allLoading() {
      return Object.values(this.loading).some((v) => v)
    },
    userCanEdit() {
      return ["Director", "Manager"].includes(this.current_user_role_name)
    },
    exportFilters() {
      return {}
    },
    selectedPickupLocation() {
      return this.pickupLocations.find((item) => String(item.id) === String(this.selectedPickupLocationId)) || null
    },
    selectedPickupLocationLabel() {
      if (!this.selectedPickupLocation) return "Tabela de Fretes"
      return `Tabela de Fretes - ${this.selectedPickupLocation.attributes?.name || ""}`
    },
    pickupLocationGroups() {
      let grouped = {}
      for (let location of this.filteredPickupLocations) {
        let brandName = location?.attributes?.brand_name || "Sem marca"
        let key = brandName.toLowerCase()
        if (!grouped[key]) {
          grouped[key] = { brand_key: key, brand_name: brandName, pickup_locations: [] }
        }
        grouped[key].pickup_locations.push(location)
      }
      return Object.values(grouped).sort((a, b) => a.brand_name.localeCompare(b.brand_name))
    },
    filteredPickupLocations() {
      if (!this.selectedBrandId) return this.pickupLocations
      return this.pickupLocations.filter((item) => Number(item?.attributes?.brand_id) === Number(this.selectedBrandId))
    },
    existingShippingRatesByRegionId() {
      let map = {}
      this.shippingRates.forEach((rate) => {
        let regionId = Number(rate?.attributes?.region_id)
        if (!Number.isNaN(regionId)) map[String(regionId)] = rate
      })
      return map
    },
    hasFilledDraftRows() {
      return Object.values(this.shippingRateDraftsByRegionId || {}).some((row) => {
        let hasKm = row?.kilometers !== "" && row?.kilometers != null
        let hasPrice = row?.unit_price !== "" && row?.unit_price != null
        let hasDischarge = row?.discharge_price !== "" && row?.discharge_price != null
        return hasKm || hasPrice || hasDischarge
      })
    },
  },
  methods: {
    getData() {
      this.getBrands()
      this.getPickupLocations()
    },
    getBrands() {
      this.loading.brands = true
      Api.Brand.index().then((res) => {
        this.brands = Array.isArray(res.data) ? res.data : []
      }).catch(() => {
        this.brands = []
      }).finally(() => {
        this.loading.brands = false
      })
    },
    getPickupLocations() {
      this.loading.pickup_locations = true
      Api.PickupLocation.index({ index_serializer: true }).then((res) => {
        this.pickupLocations = res?.data?.data || []
      }).catch(() => {
        this.pickupLocations = []
      }).finally(() => {
        this.loading.pickup_locations = false
      })
    },
    handleUpdateDraft({ regionId, field, value }) {
      let key = String(Number(regionId))
      let current = this.shippingRateDraftsByRegionId[key] || { kilometers: "", unit_price: "", discharge_price: "" }
      this.shippingRateDraftsByRegionId = {
        ...this.shippingRateDraftsByRegionId,
        [key]: {
          ...current,
          [field]: value,
        },
      }
    },
    selectPickupLocation(pickupLocation) {
      this.selectedPickupLocationId = pickupLocation.id
      this.isEditMode = false
      this.shippingRateDraftsByRegionId = {}
      this.regionTreeRefreshCounter += 1
      this.getShippingRates()
    },
    clearSelectedPickupLocation() {
      this.selectedPickupLocationId = null
      this.isEditMode = false
      this.search = null
      this.shippingRates = []
      this.shippingRateDraftsByRegionId = {}
    },
    toggleEditMode() {
      this.isEditMode = !this.isEditMode
      this.regionTreeRefreshCounter += 1
    },
    handleDoneRegionFilter(filterPayload = {}) {
      this.search = String(filterPayload.query || "").trim()
    },
    getShippingRates() {
      if (!this.selectedPickupLocationId) {
        this.shippingRates = []
        return
      }
      this.loading.shipping_rates = true
      let params = {
        pickup_location_id: this.selectedPickupLocationId,
      }
      Api.ShippingRate.index(params).then((res) => {
        this.shippingRates = res?.data?.data || []
      }).catch(() => {
        this.shippingRates = []
      }).finally(() => {
        this.loading.shipping_rates = false
      })
    },
    createFilledShippingRates() {
      if (!this.selectedPickupLocationId) return
      let rowsToCreate = Object.keys(this.shippingRateDraftsByRegionId)
        .map((regionKey) => {
          let row = this.shippingRateDraftsByRegionId[regionKey] || {}
          return {
            region_id: Number(regionKey),
            kilometers: row.kilometers,
            unit_price: row.unit_price,
            discharge_price: row.discharge_price,
          }
        })
        .filter((row) => {
          if (this.existingShippingRatesByRegionId[String(row.region_id)]) return false
          let hasKm = row.kilometers !== "" && row.kilometers != null
          let hasPrice = row.unit_price !== "" && row.unit_price != null
          let hasDischarge = row.discharge_price !== "" && row.discharge_price != null
          return hasKm || hasPrice || hasDischarge
        })
      if (!rowsToCreate.length) {
        this.addNotification({
          type: "info",
          msg: "Preencha KM e/ou preços em pelo menos uma cidade ou bairro para criar fretes.",
        })
        return
      }
      this.loading.bulk_saving = true
      let requests = rowsToCreate.map((row) => (
        Api.ShippingRate.create({
          pickup_location_id: Number(this.selectedPickupLocationId),
          region_id: Number(row.region_id),
          kilometers: Number(row.kilometers || 0),
          unit_price: Number(row.unit_price || 0),
          discharge_price: Number(row.discharge_price || 0),
        })
      ))
      Promise.allSettled(requests).then((results) => {
        let successCount = results.filter((item) => item.status === "fulfilled").length
        let failCount = results.length - successCount
        if (successCount) {
          this.addNotification({ type: "success", msg: `${successCount} frete(s) criado(s) com sucesso.` })
        }
        if (failCount) {
          this.addNotification({
            type: "warning",
            msg: `${failCount} item(ns) não foram criados. Verifique se já existem para este local.`,
          })
        }
        if (successCount) this.shippingRateDraftsByRegionId = {}
        this.getShippingRates()
      }).finally(() => {
        this.loading.bulk_saving = false
      })
    },
    saveSingleDraftRow({ regionId }) {
      if (!this.selectedPickupLocationId) return
      let key = String(Number(regionId))
      let draft = this.shippingRateDraftsByRegionId[key] || {}
      let hasKm = draft.kilometers !== "" && draft.kilometers != null
      let hasPrice = draft.unit_price !== "" && draft.unit_price != null
      let hasDischarge = draft.discharge_price !== "" && draft.discharge_price != null
      if (!hasKm && !hasPrice && !hasDischarge) return
      let existingRate = this.existingShippingRatesByRegionId[key]
      let currentKm = Number(existingRate?.attributes?.kilometers || 0)
      let currentPrice = Number(existingRate?.attributes?.unit_price || 0)
      let currentDischarge = Number(existingRate?.attributes?.discharge_price || 0)
      let nextKm = Number(hasKm ? draft.kilometers : currentKm)
      let nextPrice = Number(hasPrice ? draft.unit_price : currentPrice)
      let nextDischarge = Number(hasDischarge ? draft.discharge_price : currentDischarge)
      this.loading.bulk_saving = true
      let request = existingRate
        ? Api.ShippingRate.update({
          id: Number(existingRate.id),
          pickup_location_id: Number(this.selectedPickupLocationId),
          region_id: Number(regionId),
          kilometers: nextKm,
          unit_price: nextPrice,
          discharge_price: nextDischarge,
        })
        : Api.ShippingRate.create({
          pickup_location_id: Number(this.selectedPickupLocationId),
          region_id: Number(regionId),
          kilometers: nextKm,
          unit_price: nextPrice,
          discharge_price: nextDischarge,
        })

      request.then(() => {
        this.addNotification({ type: "success", msg: existingRate ? "Frete atualizado com sucesso." : "Frete criado com sucesso." })
        let nextDrafts = { ...this.shippingRateDraftsByRegionId }
        delete nextDrafts[key]
        this.shippingRateDraftsByRegionId = nextDrafts
        this.getShippingRates()
      }).catch(() => {
        this.addNotification({ type: "error", msg: "Não foi possível salvar o frete desta região." })
      }).finally(() => {
        this.loading.bulk_saving = false
      })
    },
  },
  mounted() {
    this.getData()
  },
  watch: {
    selectedBrandId() {
      if (this.selectedPickupLocation && Number(this.selectedPickupLocation.attributes?.brand_id) !== Number(this.selectedBrandId)) {
        this.clearSelectedPickupLocation()
      }
    },
  },
}
</script>

<style scoped>
.pickup-location-group-card {
  min-height: 220px;
}

.shipping-rate-region-filter-wrap {
  width: 100%;
}

.shipping-rate-region-filter-wrap >>> .filter-data-card {
  width: 100%;
}
</style>
